import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import ArticleCard from '@/components/base/ArticleCard';
import StatusBlock from '@/components/base/StatusBlock';
import { useAsyncData } from '@/hooks/useAsyncData';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getArticlesByCategory, getCategory, TYPE_LABEL } from '@/lib/content';
import type { Article, ContentType } from '@/types/content';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type = '' } = useParams();
  const category = getCategory(type);
  const [sub, setSub] = useState('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useDocumentTitle(
    category ? `疯狂试爱2${category.name} - 影视专题` : '疯狂试爱2 影视专题',
    category
      ? `${category.description}`
      : '浏览疯狂试爱2的影视专题内容。',
  );

  useEffect(() => {
    setSub('全部');
    setVisible(PAGE_SIZE);
  }, [type]);

  const { data, loading, error, reload } = useAsyncData<Article[]>(
    () => getArticlesByCategory(type, sub === '全部' ? undefined : sub),
    [type, sub],
  );

  const subTabs = ['全部', ...(category?.subcategories ?? [])];
  const list = data ?? [];
  const shown = list.slice(0, visible);
  const hasMore = visible < list.length;

  if (!category) {
    return (
      <>
        <PageHeader
          title="分类不存在"
          crumbs={[{ label: '首页', to: '/' }, { label: '分类' }]}
        />
        <section className="mx-auto max-w-[1280px] px-4 py-16 md:px-8">
          <StatusBlock
            icon="ri-error-warning-line"
            title="暂无相关内容"
            description="该分类暂不存在或已调整，请返回首页重新浏览。"
          />
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="TOPIC CATEGORY"
        title={`疯狂试爱2${category.name}`}
        description={category.description}
        crumbs={[{ label: '首页', to: '/' }, { label: '分类' }, { label: category.name }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setSub(tab);
                setVisible(PAGE_SIZE);
              }}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                sub === tab
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {loading && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-44 w-full animate-pulse bg-background-100"></div>
                <div className="space-y-3 p-5">
                  <div className="h-4 w-4/5 animate-pulse rounded bg-background-100"></div>
                  <div className="h-3 w-full animate-pulse rounded bg-background-100"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <StatusBlock icon="ri-wifi-off-line" title="网络异常" description={error} actionLabel="重新加载" onAction={reload} />
        )}

        {!loading && !error && list.length === 0 && (
          <StatusBlock
            icon="ri-inbox-archive-line"
            title="暂无相关内容"
            description="该子分类下暂时没有内容，可切换其他标签或返回首页。"
          />
        )}

        {!loading && !error && shown.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            <div className="mt-10 flex items-center justify-center">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              ) : (
                <p className="text-sm text-foreground-400">已经到底啦 · 共 {list.length} 条内容</p>
              )}
            </div>
          </>
        )}

        <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="font-heading text-base font-semibold text-foreground-950">浏览其他分类</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {(['plot', 'news', 'feature'] as ContentType[]).map((key) => (
              <Link
                key={key}
                to={`/category/${key}`}
                className={`whitespace-nowrap rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                  key === type
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {TYPE_LABEL[key]}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}